import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";
import { IMAGES, GENRES, STATS, MANIFESTO, RIDDIMS } from "@/content";

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [v, setV] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1800;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref} className="font-display font-800 text-4xl sm:text-5xl text-neon">
      {v}
      {suffix}
    </span>
  );
};

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-20 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(0,240,255,0.06),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="The DJ"
          title={<>Meet <span className="text-neon">QT 2hype</span></>}
        />

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <Reveal className="relative">
            <div className="absolute -inset-3 border border-volt/40 rounded-2xl rotate-2" aria-hidden="true" />
            <div className="absolute -top-10 -left-10 w-56 h-56 bg-volt/20 blur-[90px] rounded-full" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={IMAGES.portrait}
                alt="Portrait of DJ QT 2hype at the turntables under neon light"
                loading="lazy"
                className="w-full object-cover aspect-[4/5] hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-volt via-cy to-mag" />
            </div>
            <div className="absolute -bottom-5 -right-2 sm:right-6 glass rounded-xl px-5 py-3 shadow-[0_0_25px_rgba(0,240,255,0.2)]">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-cy">Latest Riddims</p>
              <p className="font-head font-bold uppercase text-sm mt-1">Jab Ice x Wuk Up Again</p>
            </div>
          </Reveal>

          <div>
            <Reveal delay={0.1}>
              <p className="text-zinc-300 text-base md:text-lg leading-relaxed">
                QT 2hype — a <span className="text-volt font-semibold">
                  <span className="text-mag font-extrabold">Q</span>uality{" "}
                  <span className="text-mag font-extrabold">T</span>ouch{" "}
                  <span className="text-mag font-extrabold">2</span>{" "}
                  <span className="text-mag font-extrabold">H</span>ype{" "}
                  <span className="text-mag font-extrabold">Y</span>our{" "}
                  <span className="text-mag font-extrabold">P</span>arty{" "}
                  <span className="text-mag font-extrabold">E</span>asily
                </span> —
                is one of London's top and most wanted Soca DJs, and has been for over 30 years. Multi-talented and
                multi-award winning, his high-energy, full-throttle hyper sets have made him a fixture of the global
                carnival scene.
              </p>
              <p className="mt-5 text-zinc-400 text-base leading-relaxed">
                He's played the biggest fetes and major carnivals around the world — Trinidad, Grenada, New York,
                Barbados, Miami, Canada, St Lucia, Turkey, Greece, Ibiza, Portugal and Gran Canaria — and of course
                London's very own Notting Hill, where you'll currently catch him on top of the Island Mas truck.
              </p>
              <p className="mt-5 text-zinc-400 text-base leading-relaxed">
                On air, he's lit up BBC 1Xtra, Bang FM (now Beat FM), Soca FM, Unique 101.2 FM and Tempo FM — home of
                the unmissable, unstoppable Hypa Mix Show and the legendary 8-hour Christmas Special that gave away
                everything from fete tickets and phone credit to rotis from the local Trini restaurant.
              </p>
            </Reveal>

            <Reveal delay={0.15} className="mt-7">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Recognition</p>
              <div className="flex flex-wrap gap-2">
                {["2x Calabash Awards '08", "Soca Music Awards '09", "Calabash Award '11", "2x Soca Music Awards '12"].map((a) => (
                  <span
                    key={a}
                    data-testid={`award-chip-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="font-mono text-[11px] uppercase tracking-[0.12em] px-4 py-2 rounded-full border border-volt/40 text-volt"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2} className="mt-6 glass rounded-xl px-5 py-4 border-l-2 border-l-volt">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-volt mb-1.5">Producer Too</p>
              <p className="text-zinc-300 text-sm leading-relaxed">
                Remixes, road mixes and edits, plus riddims for artists. Out now:{" "}
                <a
                  href={RIDDIMS.jabIce}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="riddim-jab-ice"
                  className="text-white font-semibold underline decoration-volt/60 underline-offset-4 hover:text-volt transition-colors"
                >
                  Jab Ice Riddim
                </a>{" "}
                &amp;{" "}
                <a
                  href={RIDDIMS.wukUp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="riddim-wuk-up"
                  className="text-white font-semibold underline decoration-[#1DB954]/60 underline-offset-4 hover:text-[#1DB954] transition-colors"
                >
                  Wuk Up Again Riddim
                </a>{" "}
                — check them out.
              </p>
            </Reveal>

            <Reveal delay={0.2} className="mt-7 flex flex-wrap gap-2">
              {GENRES.map((g) => (
                <span
                  key={g}
                  data-testid={`genre-badge-${g.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-mono text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full border border-white/15 text-zinc-300 hover:border-volt/60 hover:text-volt transition-colors"
                >
                  {g}
                </span>
              ))}
            </Reveal>

            <div className="mt-10 space-y-5">
              {MANIFESTO.map((m, i) => (
                <Reveal key={m.n} delay={0.15 + i * 0.1} className="flex gap-5 items-start group">
                  <span className="font-display font-800 text-2xl text-outline group-hover:text-volt group-hover:[-webkit-text-stroke:0px] transition-all">
                    {m.n}
                  </span>
                  <div>
                    <h3 className="font-head font-bold uppercase tracking-wide text-lg">{m.title}</h3>
                    <p className="text-zinc-400 text-sm mt-1 leading-relaxed">{m.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="glass rounded-2xl p-6 sm:p-8 text-center hover:border-volt/40 transition-colors">
              <div data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Counter to={s.value} suffix={s.suffix} />
                <p className="mt-3 font-mono text-[11px] tracking-[0.25em] uppercase text-zinc-400">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
